'use client';

import { c as _c } from "react/compiler-runtime";
import type { Variants } from 'motion/react';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { FocusEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { CompetitionAboutSheet } from '@/components/social/competition/competition-about-sheet';
import { CompetitionHero } from '@/components/social/competition/competition-hero';
import type { TradingCompetitionSummary } from '@/components/social/competition/types';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { competitionPath } from '@/lib/competitions/paths';
export interface CompetitionBannerProps {
  /** Published competitions, including concluded ones, ordered by the data owner. */
  slides: readonly TradingCompetitionSummary[];
  initialNowMs: number | null;
  timeZone: string;
}
const AUTO_CYCLE_INTERVAL_MS = 7_000;

// The same slide treatment as the Live Events hero (live-hero.tsx): the outgoing
// card fades out in the direction of travel, the incoming card fades in from
// the opposite side, and `mode="wait"` keeps the banner height stable.
const SLIDE_OFFSET_PX = 16;
const SLIDE_VARIANTS: Variants = {
  enter: (direction: number) => ({
    opacity: 0,
    x: SLIDE_OFFSET_PX * direction
  }),
  center: {
    opacity: 1,
    x: 0
  },
  exit: (direction: number) => ({
    opacity: 0,
    x: -SLIDE_OFFSET_PX * direction
  })
};
const STATIC_VARIANTS: Variants = {
  enter: {
    opacity: 1,
    x: 0
  },
  center: {
    opacity: 1,
    x: 0
  },
  exit: {
    opacity: 1,
    x: 0
  }
};

// One carousel at the top of the Social page: the pinned competition first,
// then live/upcoming competitions and the archive. Auto-rotates every
// seven seconds, pauses while hovered or focused, and stays put under reduced
// motion. Renders nothing when the gate is off or nothing has been published.
export function CompetitionBanner(props) {
  const $ = _c(2);
  const enabled = useFeatureGate("trading_competitions");
  if (enabled !== true || props.slides.length === 0) {
    return null;
  }
  let t0;
  if ($[0] !== props) {
    t0 = <CompetitionBannerCarousel {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function CompetitionBannerCarousel(t0) {
  const $ = _c(52);
  const {
    slides,
    initialNowMs,
    timeZone
  } = t0;
  const t = useTranslations("competitions.hero");
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const count = slides.length;
  const safeIndex = (index % count + count) % count;
  const competition = slides[safeIndex] ?? slides[0];
  let t1;
  if ($[0] !== count) {
    t1 = function previous() {
      setDirection(-1);
      setIndex(current => (current - 1 + count) % count);
    };
    $[0] = count;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previous = t1;
  let t2;
  if ($[2] !== count) {
    t2 = function next() {
      setDirection(1);
      setIndex(current_0 => (current_0 + 1) % count);
    };
    $[2] = count;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const next = t2;
  let t3;
  let t4;
  if ($[4] !== aboutOpen || $[5] !== count || $[6] !== index || $[7] !== isPaused || $[8] !== reduceMotion) {
    t3 = () => {
      if (count <= 1 || isPaused || aboutOpen || reduceMotion) {
        return;
      }
      const timer = window.setTimeout(() => {
        setDirection(1);
        setIndex((index + 1) % count);
      }, AUTO_CYCLE_INTERVAL_MS);
      return () => window.clearTimeout(timer);
    };
    t4 = [count, index, isPaused, aboutOpen, reduceMotion];
    $[4] = aboutOpen;
    $[5] = count;
    $[6] = index;
    $[7] = isPaused;
    $[8] = reduceMotion;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function handleBlur(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setIsPaused(false);
      }
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleBlur = t5;
  if (competition === undefined) {
    return null;
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("bannerLabel");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsPaused(true);
    t8 = () => setIsPaused(false);
    t9 = () => setIsPaused(true);
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  const t10 = competition.id;
  const t11 = reduceMotion ? STATIC_VARIANTS : SLIDE_VARIANTS;
  const t12 = reduceMotion ? 0 : 0.22;
  let t13;
  if ($[17] !== t12) {
    t13 = {
      duration: t12,
      ease: "easeOut"
    };
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== competition.id) {
    t14 = competitionPath(competition.id);
    $[19] = competition.id;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setAboutOpen(true);
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = slides[(safeIndex - 1 + count) % count]?.title ?? competition.title;
  const t17 = slides[(safeIndex + 1) % count]?.title ?? competition.title;
  let t18;
  if ($[22] !== count || $[23] !== next || $[24] !== previous || $[25] !== safeIndex || $[26] !== t16 || $[27] !== t17) {
    t18 = {
      count,
      index: safeIndex,
      previousTitle: t16,
      nextTitle: t17,
      onPrevious: previous,
      onNext: next
    };
    $[22] = count;
    $[23] = next;
    $[24] = previous;
    $[25] = safeIndex;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== competition || $[30] !== initialNowMs || $[31] !== t14 || $[32] !== t18 || $[33] !== timeZone) {
    t19 = <CompetitionHero competition={competition} size="compact" initialNowMs={initialNowMs} timeZone={timeZone} href={t14} onOpenInfo={t15} carousel={t18} />;
    $[29] = competition;
    $[30] = initialNowMs;
    $[31] = t14;
    $[32] = t18;
    $[33] = timeZone;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== competition.id || $[36] !== direction || $[37] !== t11 || $[38] !== t13 || $[39] !== t19) {
    t20 = <m.div key={t10} custom={direction} variants={t11} initial="enter" animate="center" exit="exit" transition={t13}>{t19}</m.div>;
    $[35] = competition.id;
    $[36] = direction;
    $[37] = t11;
    $[38] = t13;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== direction || $[42] !== t20) {
    t21 = <LazyMotion features={domAnimation}><AnimatePresence initial={false} mode="wait" custom={direction}>{t20}</AnimatePresence></LazyMotion>;
    $[41] = direction;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== aboutOpen || $[45] !== competition || $[46] !== timeZone) {
    t22 = <CompetitionAboutSheet competition={competition} timeZone={timeZone} open={aboutOpen} onOpenChange={setAboutOpen} />;
    $[44] = aboutOpen;
    $[45] = competition;
    $[46] = timeZone;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t21 || $[49] !== t22 || $[50] !== t6) {
    t23 = <section data-testid="competition-banner" aria-label={t6} onMouseEnter={t7} onMouseLeave={t8} onFocusCapture={t9} onBlurCapture={handleBlur}>{t21}{t22}</section>;
    $[48] = t21;
    $[49] = t22;
    $[50] = t6;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  return t23;
}